import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Clock, CalendarX2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCalendarFresh, readCalendarCache, CalBlock, DAYS, hhmm, isActiveOn, ymd } from '../classes/api';
import { Skeleton } from '../../components/Skeleton';
import EmptyState from '../../components/EmptyState';
import TimeGrid, { TimeGridBlock, TimeGridColumn } from '../../components/TimeGrid/TimeGrid';
import { isRentaOEnsayo } from '../../utils/classKind';
import ReceivablesPanel from './ReceivablesPanel';
import BirthdayAlert from './BirthdayAlert';
import MissedLastClassPanel from './MissedLastClassPanel';
import EventStatusPanel from './EventStatusPanel';
import TasksPanel from './TasksPanel';
import TodayPaymentsPanel from './TodayPaymentsPanel';
import PayrollWeekPanel from './PayrollWeekPanel';
import ComisionPorEntregar from '../finances/components/ComisionPorEntregar';

/** Minutos desde medianoche de la hora local actual. */
function currentMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}
/** "HH:MM:SS" → minutos desde medianoche. */
function toMin(t: string) {
  const [h, m] = (t || '').split(':');
  return Number(h) * 60 + Number(m);
}

/** Ventana (min) antes del inicio para considerar una clase "próxima a comenzar". */
const SOON_WINDOW = 30;

// El ORDEN de este dashboard salió del Word de Karla:
// 1) Clases del día (+semana) · 2) Tareas · 3) Deudas · 4) Cumpleañeros · 5) Pagos del día · 6) Evento · 7) Faltaron a su última clase.
// Con dos cambios posteriores de Carlos, ambos por la misma razón (lo urgente arriba):
//   28-jul → Cumpleaños dejó de ser tarjeta y pasó a barra a todo lo ancho (es un aviso).
//    7-ago → «Por cobrar» se cambió con «Tareas»: sube junto al calendario, Tareas baja.
export default function DashboardPage() {
  const { hasModule } = useAuth();
  const navigate = useNavigate();
  // Arranca con el último calendario conocido (caché local) → pinta al instante;
  // el fetch de fondo lo actualiza si hubo cambios.
  const [calendar, setCalendar] = useState<CalBlock[]>(() => readCalendarCache() || []);
  const [nowMin, setNowMin] = useState(currentMinutes());

  useEffect(() => {
    getCalendarFresh().then(setCalendar).catch(() => {});
  }, []);

  // Reloj vivo: refresca el estado en-curso/pronto de las clases cada minuto.
  useEffect(() => {
    const id = setInterval(() => setNowMin(currentMinutes()), 60_000);
    return () => clearInterval(id);
  }, []);

  const todayWeekday = new Date().getDay();
  const todayYmd = ymd(new Date());

  // El horario es semanal, pero cada clase tiene VIGENCIA (start_date/end_date, como en
  // Kydemy): una clase del ciclo pasado no debe seguir apareciendo "hoy".
  const todayClasses = useMemo(() =>
    calendar.filter((b) => b.weekday === todayWeekday && isActiveOn(b, todayYmd))
      .sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [calendar, todayWeekday, todayYmd]);

  // Si NINGUNA clase está vigente hoy, el "día libre" se explica por el fin del ciclo
  // (no es que se hayan borrado). Devuelve la fecha del último día del ciclo, ya formateada.
  // Ojo: son DOS casos distintos — el ciclo ya terminó (falta clonar) o el ciclo nuevo aún no
  // arranca (ya está clonado y empieza el lunes). El mensaje cambia por completo.
  const cicloTerminado = useMemo(() => {
    if (!calendar.length || calendar.some((b) => isActiveOn(b, todayYmd))) return null;
    const fmt = (d: string, weekday = false) => new Date(`${d}T00:00:00`)
      .toLocaleDateString('es-MX', { ...(weekday ? { weekday: 'long' as const } : {}), day: 'numeric', month: 'long' });
    const starts = (calendar.map((b) => b.start_date).filter(Boolean) as string[]).sort();
    if (starts.length && starts[0] > todayYmd) return { proximo: fmt(starts[0], true) };
    const ends = (calendar.map((b) => b.end_date).filter(Boolean) as string[]).sort();
    if (!ends.length) return null;
    return { fin: fmt(ends[ends.length - 1]) };
  }, [calendar, todayYmd]);

  // En el inicio solo interesa "lo que sigue": ocultamos las clases ya terminadas.
  const upcomingToday = useMemo(() =>
    todayClasses.filter((b) => toMin(b.end_time) > nowMin), [todayClasses, nowMin]);
  const allTodayDone = todayClasses.length > 0 && upcomingToday.length === 0;

  // Timeline del día estilo Kydemy (TimeGrid): columnas = salones con clase HOY.
  const salones = useMemo(() =>
    [...new Set(todayClasses.map((b) => b.classroom || 'Sin salón'))].sort(), [todayClasses]);
  const gridColumns: TimeGridColumn[] = useMemo(() =>
    salones.map((s) => ({ id: s, label: s })), [salones]);
  const gridBlocks: TimeGridBlock[] = useMemo(() => todayClasses.map((b) => {
    const start = toMin(b.start_time), end = toMin(b.end_time);
    const isNow = nowMin >= start && nowMin < end;
    const mins = start - nowMin;
    const isSoon = !isNow && mins >= 0 && mins <= SOON_WINDOW;
    return {
      key: String(b.schedule_id),
      colId: b.classroom || 'Sin salón',
      startMin: start,
      endMin: end,
      color: b.color,
      muted: isRentaOEnsayo(b),
      dimmed: nowMin >= end,
      ring: isNow,
      onClick: () => navigate(`/clases/${b.class_id}`),
      content: (
        <div className="px-1.5 py-0.5">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[11px] font-semibold">{b.name}</span>
            {isNow && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-black/25 px-1.5 text-[9px] font-semibold">
                <span className="h-1 w-1 animate-pulse rounded-full bg-current" /> En curso
              </span>
            )}
            {isSoon && (
              <span className="shrink-0 rounded-full bg-black/25 px-1.5 text-[9px] font-semibold">
                {mins === 0 ? 'ya' : `en ${mins} min`}
              </span>
            )}
          </div>
          <div className="truncate text-[10px] opacity-85">{hhmm(b.start_time)}–{hhmm(b.end_time)}</div>
        </div>
      ),
    };
  }), [todayClasses, nowMin]);

  return (
    <div className="space-y-6">
      {/* El hero de saludo se retiró (tapaba el menú de usuario; decisión de Carlos 2026-07-01). */}

      {/* Cumpleaños como BARRA a todo lo ancho (es un aviso, no un panel) — reforma 28-jul. */}
      {hasModule('birthdays') && <BirthdayAlert />}

      {/* Reforma ancho 28-jul (OK Carlos): en monitores anchos (2xl=1536+, para que el
          timeline no se apriete en laptops 1280) tres columnas — Clases de hoy como
          ancla + dos PILAS de tarjetas chicas (nada de parejas que fuerzan filas del
          mismo alto). En laptop (lg) queda timeline | pila A arriba y la pila B abajo
          en dos sub-columnas; en móvil todo apilado. items-start = cada pila a su ritmo. */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2 2xl:grid-cols-3">

      {/* Columna izquierda en laptop (lg): Clases de hoy + pila B debajo, para que Pagos del día no
          quede bajo la pila A dejando un hueco junto al calendario (Carlos 12-oct, al crecer la pila A
          con Desfasados). En móvil y en 2xl el envoltorio es `contents` y el orden lo dan los order-*:
          móvil = calendario, pila A, pila B; 2xl = tres columnas como antes. */}
      <div className="contents lg:grid lg:grid-cols-1 lg:items-start lg:gap-6 2xl:contents">
      {/* 1) Clases del día — gateado por letsmove_classes */}
      {hasModule('letsmove_classes') && (
      <div className="order-1 rounded-2xl border border-line bg-surface p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-content"><Clock size={16} className="text-accent-ink" /> Clases de hoy</h3>
          <div className="flex shrink-0 items-center gap-3">
            <Link to="/clases?vista=semana" className="text-xs font-medium text-accent-ink hover:underline">Ver semana →</Link>
            <Link to="/clases" className="text-xs font-medium text-brand-blue hover:underline">Ver horario →</Link>
          </div>
        </div>
        {calendar.length === 0 ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : todayClasses.length === 0 ? (
          <EmptyState compact icon={CalendarX2} title="Día libre"
            subtitle={cicloTerminado?.proximo
              ? `El ciclo nuevo arranca el ${cicloTerminado.proximo}: tus clases ya están listas con sus alumnos.`
              : cicloTerminado?.fin
                ? `El ciclo anterior terminó el ${cicloTerminado.fin} — las clases volverán al calendario al clonarlas al ciclo nuevo.`
                : 'No hay clases programadas para hoy.'} />
        ) : (
          <div>
            {allTodayDone && (
              <p className="mb-2 text-xs text-content-3">Terminaron las clases de hoy 🎉 — el día quedó así:</p>
            )}
            {/* Timeline estilo Kydemy: eje de horas + salones lado a lado (pedido Parte 2 de Karla).
                Día completo 8–22 visible sin scroll, con celdas compactas (pedido Carlos 2-jul). */}
            <TimeGrid columns={gridColumns} blocks={gridBlocks} hourHeight={34}
              hourStart={8} hourEnd={22} nowLine minColWidth={140} />
          </div>
        )}
      </div>
      )}

      {/* Pila B: Pagos del día · Evento · Faltó a su última clase.
          En lg va debajo de Clases de hoy (columna izquierda); en 2xl es la 3ª columna. */}
      <div className="order-3 grid grid-cols-1 gap-6">
        {/* Pagos del día también para la cajera (solo ve lo de hoy, igual que en Caja › Ingresos). */}
        {(hasModule('letsmove_finances') || hasModule('cashbox')) && <TodayPaymentsPanel />}
        {hasModule('letsmove_events') && <EventStatusPanel />}
        {hasModule('letsmove_classes') && <MissedLastClassPanel />}
      </div>
      </div>

      {/* Pila A: Por cobrar · Tareas · Nómina.
          «Por cobrar» va PRIMERO, pegado al calendario (Carlos 7-ago): es la tarjeta que hay que
          ver al abrir el sistema y antes quedaba debajo de Tareas, o sea fuera de la primera
          pantalla en laptop. Tareas baja a segundo: es alta y se consulta, no se vigila. */}
      <div className="order-2 grid grid-cols-1 gap-6">
        {/* Por cobrar también para la cajera (Caja sin Finanzas, Carlos 29-sep): cobra y debe verlo al entrar. */}
        {(hasModule('letsmove_finances') || hasModule('cashbox')) && <ReceivablesPanel />}
        {/* Desfasados en su PROPIA tarjeta, aparte de Por cobrar (Mabel 12-oct-2026). */}
        {(hasModule('letsmove_finances') || hasModule('cashbox')) && <ReceivablesPanel modo="desfasados" />}
        {/* Comisión de Noli por entregar (Mabel 5-oct): la cajera ve cuánto sacarle de caja sin abrir el Excel. */}
        {(hasModule('letsmove_finances') || hasModule('cashbox')) && <ComisionPorEntregar modo="inicio" />}
        {hasModule('tasks') && <TasksPanel />}
        {hasModule('letsmove_payroll') && <PayrollWeekPanel />}
      </div>


      </div>

      <p className="text-center text-xs text-content-3">{DAYS[todayWeekday]} · Let's Move Dance &amp; Fitness</p>
    </div>
  );
}
